import {
  Map as ImmutableMap,
  Set as ImmutableSet,
  OrderedSet as ImmutableOrderedSet,
} from 'immutable';

/* Constants */
import {
  ADD_TO_CART, CART_ACTION_STATE,
  CART_CHECKOUT_TYPES,
  DEFAULT_QUANTITY_FOR_CART,
} from './cartConstants';

/* Components */
import VariantPropertyAvailability from '../../views/variant/property/VariantPropertyAvailability';
import VariantProperty from '../../views/variant/property/VariantProperty';
import VariantPropertySelection from '../../views/variant/property/VariantPropertySelection';

/* Actions */
import {actionCreators as pendingActionCreators} from '../../redux/actions/pendingActions';

function generateAddToCartRequest(product, quantity = DEFAULT_QUANTITY_FOR_CART) {
  return {
    catalogName: product.catalogName,
    checkoutType: extractCheckoutTypeFromProduct(),
    listingId: product.listingId,
    productId: product.productId,
    quantity: quantity,
    sku: product.sku,
    statisticsBuyboxModel: null,
  };
}

function generateCheckoutVoltranAddToCartRequest(dispatch, product, quantity = DEFAULT_QUANTITY_FOR_CART) {
  dispatch(pendingActionCreators.setPendingState(CART_ACTION_STATE, true))
  return {
    listingId: product.listingId,
    quantity: quantity,
    sku: product.sku,
    adInfoEvent: product.adInfo ? product.productUrl : null,
  };
}

function extractCheckoutTypeFromProduct() {
  return CART_CHECKOUT_TYPES.OTHER
}

function generateVariantPropertyAvailabilityMap(variants) {
  const mutableMap = new Map();

  if (variants) {
    variants.forEach((variant) => {
      variant.properties.forEach(
        (property) => {
          if (!mutableMap.has(property.name)) {
            mutableMap.set(property.name, ImmutableOrderedSet());
          }

          let variantPropertySet = mutableMap.get(property.name);

          mutableMap.set(
            property.name,
            variantPropertySet.add(
              new VariantPropertyAvailability(
                new VariantProperty({
                  value: property.value,
                  name: property.name,
                  isSelected: variant.isDefault,
                  imageUrl: variant.imageUrl,
                }),
                {
                  isAvailable: null,
                },
              )
            ));
        }
      );
    });
  }

  return ImmutableMap(mutableMap);
}

function generateVariantMap(variants) {
  let mutableMap = new Map();

  if (variants) {
    variants.forEach((variant) => {
      const propertyArray = [];

      variant.properties.forEach(
        (property) => {
          propertyArray.push(
            new VariantProperty({
              value: property.value,
              name: property.name,
              isSelected: variant.isDefault,
              imageUrl: variant.imageUrl,
            })
          );
        });

      mutableMap.set(
        ImmutableSet(propertyArray),
        variant
      );
    });
  }

  return ImmutableMap(mutableMap);
}

function generateInitialVariantPropertySelectionMap(variants) {
  const mutableMap = new Map();

  if (variants) {
    variants.forEach(
      variant => {
        if (variant.isDefault) {
          variant.properties.forEach(property => {
            mutableMap.set(
              property.name,
              new VariantPropertySelection(
                new VariantProperty({
                  value: property.value,
                  name: property.name,
                  isSelected: variant.isDefault,
                  imageUrl: variant.imageUrl,
                })
              ))
          });
        }
      });
  }

  return ImmutableMap(mutableMap);
}

function updateVariantPropertyAvailabilityMap(
  variantPropertyAvailabilityMap,
  variantPropertySelectionMap,
  variantMap,
) {
  const selectedVariantPropertyArray = Array.from(variantPropertySelectionMap.values()).map(
    variantPropertySelection => variantPropertySelection.variantProperty
  );

  return variantPropertyAvailabilityMap.withMutations(mutableMap => {
    for (const [variantPropertyName, variantPropertyAvailabilitySet] of mutableMap.entries()) {
      variantPropertyAvailabilitySet.forEach(
        variantPropertyAvailability => {
          const candidateVariantSet =
            ImmutableSet(
              selectedVariantPropertyArray.
                filter(
                  vp => vp.name !== variantPropertyAvailability.variantProperty.name
                )
            ).add(
              variantPropertyAvailability.variantProperty
            );

          const candidateVariant = variantMap.get(candidateVariantSet);

          mutableMap.set(variantPropertyName,
            mutableMap.get(variantPropertyName).add(
              new VariantPropertyAvailability(
                variantPropertyAvailability.variantProperty,
                {
                  isAvailable: candidateVariant && (
                    candidateVariant.procurable || candidateVariant.inStockDate
                  ),
                }
              )
            ));
        }
      );
    }
  });
}

function updateSelectedVariantPropertyIndexMap(
  variantPropertySelectionMap,
  variantPropertyAvailabilityMap
) {
  const indexEntries = [];

  for (const [propertyName, variantPropertySelection] of variantPropertySelectionMap.entries()) {
    const availabilitySet = variantPropertyAvailabilityMap.get(propertyName);
    const selectedIndex = availabilitySet.toIndexedSeq().findIndex(
      variantPropertyAvailability => variantPropertySelection.variantProperty.equals(
        variantPropertyAvailability.variantProperty
      )
    );

    indexEntries.push([propertyName, selectedIndex]);
  }

  return ImmutableMap(indexEntries);
}

function statusMessage(status, message = null) {
  if(status && process.env.BROWSER) {
    window.hbToast.show({
      type: ADD_TO_CART.TYPE.SUCCESS,
      title: ADD_TO_CART.TITLE.SUCCESS,
    })
  } else {
    window.hbToast.show({
      type: ADD_TO_CART.TYPE.ERROR,
      title: ADD_TO_CART.TITLE.FAIL,
      message: message && message !== '' ? message : ADD_TO_CART.MESSAGE.FAIL,
    })
  }
}

function generateAddToCartMapper(response) {
  // Todo This function workaround solution when we use checkout voltran addtocart remove this function.
  return {
    data: {
      count: response?.data?.data?.count || 0,
      checkoutUrl: response?.data?.data?.checkoutUrl || '',
      success: response?.data?.data?.success || false,
      failMessage: response?.data?.failMessage || '',
    },
  }
}

export {
  generateAddToCartRequest,
  extractCheckoutTypeFromProduct,
  generateVariantPropertyAvailabilityMap,
  generateVariantMap,
  generateCheckoutVoltranAddToCartRequest,
  generateInitialVariantPropertySelectionMap,
  updateVariantPropertyAvailabilityMap,
  updateSelectedVariantPropertyIndexMap,
  statusMessage,
  generateAddToCartMapper,
};
